import React, { useState, useEffect } from 'react'
import { 
  QrCode, 
  Copy, 
  Check, 
  ExternalLink, 
  AlertCircle,
  Download,
  X,
  CheckCircle,
  DollarSign,
  User,
  Calendar,
  FileText
} from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useLinkPixStorage } from '../hooks/useLocalStorage'

export default function ResultadoLinkAleatorio() {
  // Estados
  const [linkData, setLinkData] = useState(null)
  const [erro, setErro] = useState(null)
  const [copiado, setCopiado] = useState({
    checkout: false,
    pix: false
  })
  const [gerandoPdf, setGerandoPdf] = useState(false)
  const [sucesso, setSucesso] = useState(null)

  // ⭐ HOOK DE LOCALSTORAGE
  const { obterLinkPix, removeLinkData, temDados } = useLinkPixStorage()

  /**
   * Carregar dados do localStorage ao montar
   */
  useEffect(() => {
    try {
      console.log('📥 Carregando dados do link PIX...')

      // ⭐ VERIFICAR SE HÁ DADOS VÁLIDOS USANDO HOOK
      if (!temDados()) {
        setErro('Nenhum dado encontrado ou dados expiraram (>24h). Por favor, gere um novo link PIX.')
        console.warn('⚠️ Nenhum dado válido no localStorage')
        return
      }

      // ⭐ OBTER DADOS USANDO HOOK
      const dados = obterLinkPix()
      
      if (!dados) {
        setErro('Erro ao carregar dados do link PIX. Por favor, gere um novo link PIX.')
        console.error('❌ Dados retornaram null/undefined')
        return
      }

      // ⚠️ VERIFICAÇÃO ADICIONAL: Dados não muito antigos (10 minutos)
      const dataAtual = Date.now()
      const dataGerado = dados.timestamp || dataAtual
      const diferencaMinutos = (dataAtual - dataGerado) / 1000 / 60
      
      if (diferencaMinutos > 10) {
        console.warn(`⚠️ Dados com ${diferencaMinutos.toFixed(1)} minutos - recomendável gerar novo link`)
      }
      
      setLinkData(dados)
      console.log('✅ Dados carregados com sucesso:', dados)
      console.log(`📊 Link gerado há ${diferencaMinutos.toFixed(1)} minutos`)
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error)
      setErro('Erro ao carregar dados do link PIX.')
    }
  }, [obterLinkPix, temDados, removeLinkData])

  /**
   * Copiar texto para área de transferência
   */
  const copiarTexto = (texto, tipo) => {
    navigator.clipboard.writeText(texto)
      .then(() => {
        setCopiado(prev => ({ ...prev, [tipo]: true }))
        setTimeout(() => {
          setCopiado(prev => ({ ...prev, [tipo]: false }))
        }, 2000)
      })
      .catch(err => {
        console.error('Erro ao copiar:', err)
        alert('Erro ao copiar para área de transferência')
      })
  }

  /**
   * ⭐ BAIXAR PDF (IGUAL AO COBRANCAPIX.JSX)
   */
  const baixarPDF = async () => {
    try {
      setGerandoPdf(true)
      console.log('📄 Gerando PDF...')
      
      // Importar dinamicamente jsPDF
      const { default: jsPDF } = await import('jspdf')
      
      const doc = new jsPDF()
      
      // Configurações
      const pageWidth = doc.internal.pageSize.getWidth()
      const margin = 20
      let yPosition = 20
      
      // ====== CABEÇALHO ======
      doc.setFontSize(18)
      doc.setFont('helvetica', 'bold')
      doc.text('LINK DE PAGAMENTO PIX', pageWidth / 2, yPosition, { align: 'center' })
      yPosition += 10
      
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(100, 100, 100)
      doc.text('Dantas Eletro - Sistema de Cobrança', pageWidth / 2, yPosition, { align: 'center' })
      yPosition += 15
      
      doc.setTextColor(0, 0, 0)
      
      // ====== INFORMAÇÕES DO CLIENTE ======
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('DADOS DO CLIENTE', margin, yPosition)
      yPosition += 8
      
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      doc.text(`Nome: ${linkData.cliente?.nome || 'N/A'}`, margin, yPosition)
      yPosition += 6
      doc.text(`Código: ${linkData.cliente?.codigo || 'N/A'}`, margin, yPosition)
      yPosition += 6
      doc.text(`CPF/CNPJ: ${linkData.cliente?.cpf_cnpj || 'N/A'}`, margin, yPosition)
      yPosition += 12
      
      // ====== INFORMAÇÕES DO PAGAMENTO ======
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(12)
      doc.text('INFORMAÇÕES DO PAGAMENTO', margin, yPosition)
      yPosition += 8
      
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      
      const valorFormatado = linkData.valor_formatado || 
                           (linkData.amount ? linkData.amount.toFixed(2) : '0.00')
      doc.text(`Valor: R$ ${valorFormatado}`, margin, yPosition)
      yPosition += 6
      
      if (linkData.descricao) {
        doc.text(`Descrição: ${linkData.descricao}`, margin, yPosition)
        yPosition += 6
      }
      
      const dataGeracao = linkData.timestamp 
        ? format(new Date(linkData.timestamp), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
        : format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
      
      doc.text(`Gerado em: ${dataGeracao}`, margin, yPosition)
      yPosition += 6
      
      if (linkData.pix?.expires_at) {
        const dataExpiracao = format(new Date(linkData.pix.expires_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
        doc.setTextColor(255, 0, 0)
        doc.text(`Expira em: ${dataExpiracao}`, margin, yPosition)
        doc.setTextColor(0, 0, 0)
        yPosition += 6
      } else if (linkData.dias_expiracao) {
        doc.text(`Validade: ${linkData.dias_expiracao} dia(s)`, margin, yPosition)
        yPosition += 6
      }
      
      yPosition += 6
      
      // ====== QR CODE ======
      if (linkData.pix?.qr_code_url || linkData.link_id) {
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(12)
        doc.text('QR CODE PIX', margin, yPosition)
        yPosition += 8
        
        try {
          const img = new Image()
          
          // Tentar usar endpoint local primeiro, senão usar URL externa
          if (linkData.link_id) {
            const localImageUrl = `${window.location.origin}/api/pix/qrcode/${linkData.link_id}`
            console.log('📱 Usando QR Code local:', localImageUrl)
            img.src = localImageUrl
          } else if (linkData.pix?.qr_code_url) {
            console.log('🌐 Usando QR Code externo:', linkData.pix.qr_code_url)
            img.crossOrigin = 'Anonymous'
            img.src = linkData.pix.qr_code_url
          } else {
            throw new Error('Nenhuma fonte de QR Code disponível')
          }
          
          await new Promise((resolve, reject) => {
            img.onload = () => {
              console.log('✅ Imagem QR Code carregada')
              resolve()
            }
            img.onerror = (error) => {
              console.error('❌ Erro ao carregar QR Code:', error)
              reject(new Error('Erro ao carregar QR Code'))
            }
          })
          
          // Adicionar QR Code centralizado
          const qrSize = 80
          const qrX = (pageWidth - qrSize) / 2
          
          doc.addImage(img, 'PNG', qrX, yPosition, qrSize, qrSize)
          yPosition += qrSize + 10
          
          // Texto informativo
          doc.setFont('helvetica', 'normal')
          doc.setFontSize(9)
          doc.setTextColor(100, 100, 100)
          doc.text('Escaneie o QR Code acima com o app do seu banco', pageWidth / 2, yPosition, { align: 'center' })
          doc.setTextColor(0, 0, 0)
          yPosition += 10
          
        } catch (error) {
          console.error('❌ Erro ao adicionar QR Code ao PDF:', error)
          doc.setFont('helvetica', 'italic')
          doc.setFontSize(9)
          doc.text('(QR Code não disponível para inclusão no PDF)', margin, yPosition)
          yPosition += 10
        }
      }
      
      // ====== CÓDIGO PIX COPIA E COLA ======
      if (linkData.pix?.qr_code) {
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(10)
        doc.text('CÓDIGO PIX (Copia e Cola):', margin, yPosition)
        yPosition += 6
        
        doc.setFont('courier', 'normal')
        doc.setFontSize(8)
        
        // Quebrar texto longo em múltiplas linhas
        const qrCode = linkData.pix.qr_code
        const maxWidth = pageWidth - (margin * 2)
        const lines = doc.splitTextToSize(qrCode, maxWidth)
        
        lines.forEach(line => {
          if (yPosition > 270) {
            doc.addPage()
            yPosition = 20
          }
          doc.text(line, margin, yPosition)
          yPosition += 4
        })
        
        yPosition += 6
      }
      
      // ====== LINK DE PAGAMENTO ======
      if (linkData.checkout_url) {
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(10)
        doc.text('LINK DE PAGAMENTO:', margin, yPosition)
        yPosition += 6
        
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.setTextColor(0, 0, 255)
        
        const linkLines = doc.splitTextToSize(linkData.checkout_url, pageWidth - (margin * 2))
        linkLines.forEach(line => {
          if (yPosition > 270) {
            doc.addPage()
            yPosition = 20
          }
          doc.text(line, margin, yPosition)
          yPosition += 5
        })
        
        doc.setTextColor(0, 0, 0)
        yPosition += 6
      }
      
      // ====== INFORMAÇÕES ADICIONAIS ======
      if (linkData.pedido_id || linkData.link_id) {
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(10)
        doc.text('INFORMAÇÕES ADICIONAIS:', margin, yPosition)
        yPosition += 6
        
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        
        if (linkData.pedido_id) {
          doc.text(`ID do Pedido: ${linkData.pedido_id}`, margin, yPosition)
          yPosition += 5
        }
        
        if (linkData.link_id) {
          doc.text(`ID do Link: ${linkData.link_id}`, margin, yPosition)
          yPosition += 5
        }
      }
      
      // ====== RODAPÉ ======
      doc.setFont('helvetica', 'italic')
      doc.setFontSize(8)
      doc.setTextColor(128, 128, 128)
      doc.text(
        `Documento gerado em: ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`,
        pageWidth / 2,
        280,
        { align: 'center' }
      )
      
      // Salvar PDF
      const nomeArquivo = `Link_PIX_${linkData.cliente?.codigo || 'aleatorio'}_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`
      doc.save(nomeArquivo)
      
      console.log('✅ PDF gerado com sucesso:', nomeArquivo)
      setSucesso('PDF baixado com sucesso!')
      setTimeout(() => setSucesso(null), 3000)
      
    } catch (error) {
      console.error('❌ Erro ao gerar PDF:', error)
      setErro('Erro ao gerar PDF: ' + error.message)
      setTimeout(() => setErro(null), 5000)
    } finally {
      setGerandoPdf(false)
    }
  }

  /**
   * Fechar janela
   */
  const fecharJanela = () => {
    // ⭐ LIMPAR LOCALSTORAGE USANDO HOOK
    removeLinkData()
    console.log('🗑️ Dados removidos do localStorage')
    
    // Fechar janela
    window.close()
  }

  // Tela de erro
  if (erro) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
            Erro ao Carregar Dados
          </h2>
          <p className="text-gray-600 text-center mb-6">
            {erro}
          </p>
          <button
            onClick={fecharJanela}
            className="w-full bg-indigo-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    )
  }

  // Loading
  if (!linkData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando informações do link PIX...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl shadow-xl p-6 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <CheckCircle className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Link PIX Gerado!</h1>
                <p className="text-green-100 text-sm mt-1">
                  Compartilhe com o cliente para receber o pagamento
                </p>
              </div>
            </div>
            <button
              onClick={fecharJanela}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title="Fechar"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Mensagem de sucesso (se houver) */}
        {sucesso && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-700 font-medium">{sucesso}</p>
            </div>
          </div>
        )}

        {/* Status de Envio (se houver) */}
        {linkData.status_envio && (linkData.status_envio.whatsapp || linkData.status_envio.email) && (
          <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Status de Envio</h3>
            <div className="flex flex-wrap gap-3">
              {linkData.status_envio.whatsapp && (
                <div className="flex items-center space-x-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700 font-medium">WhatsApp Enviado</span>
                </div>
              )}
              {linkData.status_envio.email && (
                <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-700 font-medium">Email Enviado</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Card Principal */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Informações do Cliente */}
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-3">INFORMAÇÕES DO CLIENTE</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <User className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Nome</p>
                  <p className="text-sm font-medium text-gray-900">{linkData.cliente?.nome || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <FileText className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Código</p>
                  <p className="text-sm font-medium text-gray-900">{linkData.cliente?.codigo || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <FileText className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">CPF/CNPJ</p>
                  <p className="text-sm font-medium text-gray-900">{linkData.cliente?.cpf_cnpj || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <DollarSign className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Valor</p>
                  <p className="text-sm font-medium text-gray-900">R$ {linkData.valor_formatado || linkData.amount?.toFixed(2) || 'N/A'}</p>
                </div>
              </div>
            </div>
            
            {/* Descrição/Mensagem */}
            {linkData.descricao && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Descrição</p>
                <p className="text-sm text-gray-700">{linkData.descricao}</p>
              </div>
            )}
          </div>

          {/* QR Code */}
          {linkData.pix?.qr_code_url && (
            <div className="p-8 bg-white">
              <h3 className="text-sm font-medium text-gray-700 mb-4 text-center">
                QR CODE PIX
              </h3>
              <div className="flex justify-center mb-4">
                <div className="inline-block p-4 bg-white rounded-xl border-2 border-gray-200 shadow-sm">
                  <img 
                    src={linkData.pix.qr_code_url} 
                    alt="QR Code PIX"
                    className="w-64 h-64"
                  />
                </div>
              </div>
              <p className="text-center text-xs text-gray-500 mb-4">
                Escaneie com o app do seu banco
              </p>
              
              {/* ⭐ BOTÃO BAIXAR PDF */}
              <button
                onClick={baixarPDF}
                disabled={gerandoPdf}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {gerandoPdf ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Gerando PDF...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    <span>Baixar PDF</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Link de Checkout */}
          {linkData.checkout_url && (
            <div className="p-6 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link de Pagamento
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={linkData.checkout_url}
                  readOnly
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                />
                <button
                  onClick={() => copiarTexto(linkData.checkout_url, 'checkout')}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                  title="Copiar link"
                >
                  {copiado.checkout ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span className="hidden sm:inline">Copiado!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span className="hidden sm:inline">Copiar</span>
                    </>
                  )}
                </button>
                <a
                  href={linkData.checkout_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  title="Abrir link"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          )}

          {/* Código PIX Copia e Cola */}
          {linkData.pix?.qr_code && (
            <div className="p-6 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Código PIX (Copia e Cola)
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={linkData.pix.qr_code}
                  readOnly
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                />
                <button
                  onClick={() => copiarTexto(linkData.pix.qr_code, 'pix')}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                  title="Copiar código PIX"
                >
                  {copiado.pix ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span className="hidden sm:inline">Copiado!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span className="hidden sm:inline">Copiar</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Informações Adicionais */}
          <div className="p-6 bg-blue-50 border-t border-blue-100">
            <h3 className="text-sm font-medium text-gray-700 mb-3">DETALHES DO PAGAMENTO</h3>
            <div className="space-y-2">
              {linkData.pedido_id && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">ID do Pedido:</span>
                  <span className="font-mono text-xs text-gray-700">{linkData.pedido_id}</span>
                </div>
              )}
              {linkData.link_id && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">ID do Link:</span>
                  <span className="font-mono text-xs text-gray-700">{linkData.link_id}</span>
                </div>
              )}
              {linkData.dias_expiracao && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Expira em:</span>
                  <span className="font-medium text-gray-900">{linkData.dias_expiracao} dia(s)</span>
                </div>
              )}
              {linkData.pix?.expires_at && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Data de Expiração:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(linkData.pix.expires_at).toLocaleString('pt-BR')}
                  </span>
                </div>
              )}
              {linkData.timestamp && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Gerado em:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(linkData.timestamp).toLocaleString('pt-BR')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Botão Fechar */}
          <div className="p-6 border-t border-gray-200">
            <button
              onClick={fecharJanela}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>

        {/* Rodapé */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Mantenha esta janela aberta para consultar as informações do pagamento
          </p>
        </div>
      </div>
    </div>
  )
}